import { useEffect, useMemo, useState } from "react";
import { Mail, Send, Eye, FileSignature, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/auth";
import { WelcomeEmailModal } from "./WelcomeEmailModal";
import RecordActionsMenu from "./RecordActionsMenu";
import Pagination from "./Pagination";
import { welcomeButtonClass, welcomeButtonTitle, StatusDot } from "./shared";
import { devLog } from "@/lib/log";

const VendorListPanel = ({ vendors, consultations, onView, onVendorUpdated, pagination }) => {
  const [q, setQ] = useState("");
  const [tcSendingId, setTcSendingId] = useState(null);
  const [tcStatusMap, setTcStatusMap] = useState({});
  const [welcomeTarget, setWelcomeTarget] = useState(null);
  const [welcomeStatusMap, setWelcomeStatusMap] = useState({});
  const [notesSummary, setNotesSummary] = useState({});
  const [packSendingId, setPackSendingId] = useState(null);
  const [packStatusMap, setPackStatusMap] = useState({});
  const [featuredSavingId, setFeaturedSavingId] = useState(null);
  const approved = useMemo(
    () => (vendors || []).filter((v) => v.status === "approved"),
    [vendors]
  );

  // Load latest Vendor T&C send statuses on mount + after each successful send.
  const refreshTcStatuses = async () => {
    try {
      const { data } = await api.get("/admin/vendor-tc-status");
      setTcStatusMap(data || {});
    } catch (err) {
      devLog("vendor-tc-status fetch failed:", err?.message || err);
    }
  };
  const refreshWelcomeStatuses = async () => {
    try {
      const { data } = await api.get("/admin/welcome-email-status", {
        params: { role: "vendor" },
      });
      setWelcomeStatusMap(data || {});
    } catch (err) {
      devLog("welcome-email-status (vendor) fetch failed:", err?.message);
    }
  };
  const refreshNotesSummary = async () => {
    try {
      const { data } = await api.get("/admin/notes-summary", {
        params: { kind: "vendor" },
      });
      setNotesSummary(data || {});
    } catch (err) {
      devLog("notes-summary (vendor) fetch failed:", err?.message || err);
    }
  };
  const refreshPackStatuses = async () => {
    try {
      const { data } = await api.get("/admin/pack-email-status", {
        params: { role: "vendor" },
      });
      setPackStatusMap(data || {});
    } catch (err) {
      devLog("pack-email-status (vendor) fetch failed:", err?.message);
    }
  };
  useEffect(() => {
    refreshTcStatuses();
    refreshWelcomeStatuses();
    refreshNotesSummary();
    refreshPackStatuses();
  }, []);

  const toggleFeatured = async (v) => {
    if (!v?.id || featuredSavingId) return;
    const next = !v.is_featured;
    setFeaturedSavingId(v.id);
    try {
      const { data } = await api.patch(
        `/admin/vendor-applications/${v.id}/featured`,
        { is_featured: next }
      );
      // Notify parent so it can de-feature any other vendor in local state.
      onVendorUpdated?.({ id: v.id, is_featured: !!data.is_featured }, { exclusiveFeatured: !!data.is_featured });
      toast(
        next
          ? `${v.company_name} is now the Featured Vendor of the Month`
          : `${v.company_name} is no longer featured`,
        { duration: 2400 }
      );
    } catch (err) {
      toast(err?.response?.data?.detail || "Could not update featured status", {
        duration: 2800,
      });
    } finally {
      setFeaturedSavingId(null);
    }
  };

  const sendVendorPack = async (v) => {
    if (!v?.id || packSendingId) return;
    if (!v.email) {
      toast("This vendor has no email on file", { duration: 2400 });
      return;
    }
    setPackSendingId(v.id);
    try {
      const { data } = await api.post(
        `/admin/vendor-applications/${v.id}/send-pack`
      );
      toast(
        data?.sent
          ? `Onboarding pack (${data.documents_attached.length} docs) sent to ${data.recipient}`
          : `Logged — email service unavailable`,
        { duration: 3400 }
      );
      refreshNotesSummary();
      refreshPackStatuses();
    } catch (err) {
      toast(err?.response?.data?.detail || "Couldn't send the pack", {
        duration: 2800,
      });
    } finally {
      setPackSendingId(null);
    }
  };


  const sendTcToVendor = async (v) => {
    if (!v?.id || tcSendingId) return;
    if (!v.email) {
      toast("This vendor has no email on file", { duration: 2400 });
      return;
    }
    setTcSendingId(v.id);
    try {
      const { data } = await api.post(
        `/admin/vendor-applications/${v.id}/send-tc`
      );
      toast(
        data?.sent
          ? `Vendor T&C emailed to ${data.recipient}`
          : `Logged — email service unavailable (${data?.recipient || v.email})`,
        { duration: 3200 }
      );
      refreshTcStatuses();
    } catch (err) {
      toast(
        err?.response?.data?.detail || "Couldn't send the T&C — try again",
        { duration: 3200 }
      );
    } finally {
      setTcSendingId(null);
    }
  };
  // Map vendor email → consultation_notes (from any booked vendor intro slot)
  const notesByEmail = useMemo(() => {
    const m = new Map();
    (consultations || []).forEach((c) => {
      if (c.role === "vendor" && c.email && c.notes) {
        m.set(c.email.toLowerCase(), c.notes);
      }
    });
    return m;
  }, [consultations]);
  const term = q.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      !term
        ? approved
        : approved.filter((v) =>
            (v.company_name || "").toLowerCase().includes(term)
          ),
    [approved, term]
  );

  return (
    <div data-testid="section-vendor-list">
      <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
        Roster
      </span>
      <h2 className="font-serif-display mt-3 text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight">
        Vendor List
      </h2>
      <p className="mt-2 text-sm text-[var(--ld-ink-muted)] max-w-xl">
        All approved vendors in the directory. Search by company name.
      </p>

      <div className="mt-6">
        <input
          data-testid="vendor-list-search"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by company name…"
          className="w-full max-w-sm h-11 bg-transparent border border-[var(--ld-ink)]/25 px-3 text-sm text-[var(--ld-ink)] placeholder:text-[var(--ld-ink-muted)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors"
        />
      </div>

      <div className="mt-6 border border-[var(--ld-border)]/80 overflow-x-auto">
        <table className="w-full text-sm" data-testid="vendor-list-table">
          <thead className="bg-[var(--ld-ink)]/[0.03] text-[10px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)]">
            <tr>
              <th className="text-left font-accent font-normal px-4 py-3">
                Company
              </th>
              <th className="text-left font-accent font-normal px-4 py-3">
                Email
              </th>
              <th className="text-left font-accent font-normal px-4 py-3">
                Services
              </th>
              <th className="text-left font-accent font-normal px-4 py-3">
                Consultation notes
              </th>
              <th className="text-right font-accent font-normal px-4 py-3">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--ld-border)]/70">
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-10 text-center text-[var(--ld-ink-muted)] italic"
                >
                  {approved.length === 0
                    ? "No approved vendors yet."
                    : "No matches."}
                </td>
              </tr>
            )}
            {filtered.map((v) => {
              const notes =
                v.email && notesByEmail.get(v.email.toLowerCase());
              return (
                <tr
                  key={v.id}
                  data-testid={`vendor-list-row-${v.id}`}
                  className="hover:bg-[var(--ld-ink)]/[0.02]"
                >
                  <td className="px-4 py-3 font-medium text-[var(--ld-ink)]">
                    <div className="flex items-center gap-2">
                      {v.media_health?.missing_count > 0 && (
                        <span
                          data-testid={`vendor-media-health-dot-${v.id}`}
                          title={`${v.media_health.missing_count} of ${v.media_health.total} media file(s) missing from storage — ask vendor to re-upload`}
                          className="inline-block h-2 w-2 rounded-full bg-red-600 shrink-0"
                          aria-label="Media missing"
                        />
                      )}
                      <span>{v.company_name}</span>
                      {v.is_featured && (
                        <span
                          data-testid={`vendor-list-featured-pill-${v.id}`}
                          title="Featured vendor of the month — shown first on /vendor-listing"
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] tracking-[0.2em] uppercase border border-[var(--ld-gold-deep)] bg-[var(--ld-gold-soft)]/40 text-[var(--ld-gold-deep)]"
                        >
                          <Star size={9} strokeWidth={2} fill="currentColor" />
                          Featured
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[var(--ld-ink-muted)]">
                    <a
                      href={`mailto:${v.email}`}
                      className="hover:text-[var(--ld-gold-deep)]"
                    >
                      {v.email}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-[var(--ld-ink)]">
                    {(v.service_types || []).join(", ") || "—"}
                  </td>
                  <td className="px-4 py-3 text-[var(--ld-ink)] text-[13px] leading-[1.6] max-w-md whitespace-pre-wrap">
                    {(() => {
                      const summary = notesSummary[v.id];
                      const latest = summary?.latest || notes;
                      if (!latest) {
                        return (
                          <span className="italic text-[var(--ld-ink-muted)]">
                            No notes yet
                          </span>
                        );
                      }
                      return (
                        <span data-testid={`vendor-list-note-${v.id}`}>
                          {latest}
                          {summary?.count > 1 && (
                            <span className="text-[var(--ld-ink-muted)]"> · +{summary.count - 1} more</span>
                          )}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <button
                        data-testid={`vendor-list-featured-${v.id}`}
                        data-featured={v.is_featured ? "true" : "false"}
                        onClick={() => toggleFeatured(v)}
                        disabled={featuredSavingId === v.id}
                        title={
                          v.is_featured
                            ? "Remove from Featured Vendor of the Month"
                            : "Mark as Featured Vendor of the Month (replaces any current feature)"
                        }
                        className={
                          "inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border px-3 py-1.5 transition-colors disabled:opacity-60 disabled:cursor-wait " +
                          (v.is_featured
                            ? "bg-[var(--ld-gold-deep)] text-white border-[var(--ld-gold-deep)] hover:bg-[var(--ld-ink)]"
                            : "border-[var(--ld-gold-deep)]/40 text-[var(--ld-gold-deep)] hover:bg-[var(--ld-gold-deep)] hover:text-white")
                        }
                      >
                        <Star
                          size={13}
                          strokeWidth={1.7}
                          fill={v.is_featured ? "currentColor" : "none"}
                        />
                        {v.is_featured ? "Featured" : "Feature"}
                      </button>
                      <button
                        data-testid={`vendor-list-welcome-${v.id}`}
                        data-welcome-status={welcomeStatusMap[v.id]?.status || "none"}
                        onClick={() => setWelcomeTarget({ role: "vendor", id: v.id })}
                        title={welcomeButtonTitle(
                          welcomeStatusMap[v.id]?.status,
                          welcomeStatusMap[v.id]?.sent_at,
                          v.email
                        )}
                        className={`inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border px-3 py-1.5 transition-colors ${welcomeButtonClass(welcomeStatusMap[v.id]?.status)}`}
                      >
                        <Mail size={13} strokeWidth={1.6} />
                        Welcome
                      </button>
                      <button
                        data-testid={`vendor-list-send-tc-${v.id}`}
                        onClick={() => sendTcToVendor(v)}
                        disabled={tcSendingId === v.id}
                        title={(() => {
                          const s = tcStatusMap[v.id]?.status;
                          if (s === "opened") return "Vendor opened the T&C email";
                          if (["delivered", "sent"].includes(s)) return "T&C emailed — awaiting open";
                          if (["bounced", "complained", "delivery_delayed", "failed"].includes(s)) return "Last attempt did not deliver — click to retry";
                          return `Email the Vendor T&C to ${v.email || "this vendor"}`;
                        })()}
                        className="relative inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-[var(--ld-ink)]/30 px-3 py-1.5 hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)] transition-colors disabled:opacity-60 disabled:cursor-wait"
                      >
                        <Send size={13} strokeWidth={1.6} />
                        {tcSendingId === v.id ? "Sending…" : "Send T&C"}
                        {(() => {
                          const s = tcStatusMap[v.id]?.status;
                          if (s === "opened") {
                            return (
                              <span
                                data-testid={`vendor-list-tc-status-${v.id}`}
                                data-status="opened"
                                title="Vendor opened the email"
                                className="ml-1 inline-block h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-emerald-500/25"
                              />
                            );
                          }
                          if (["delivered", "sent"].includes(s)) {
                            return (
                              <span
                                data-testid={`vendor-list-tc-status-${v.id}`}
                                data-status="sent"
                                title="T&C emailed — awaiting open"
                                className="ml-1 inline-block h-2 w-2 rounded-full bg-amber-400 ring-2 ring-amber-400/30"
                              />
                            );
                          }
                          return null;
                        })()}
                      </button>
                      <button
                        data-testid={`vendor-list-send-pack-${v.id}`}
                        onClick={() => sendVendorPack(v)}
                        disabled={packSendingId === v.id}
                        title={`Email the full onboarding pack (T&C + policies) to ${v.email || "this vendor"}`}
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-[var(--ld-ink)]/40 text-[var(--ld-ink)] bg-[var(--ld-gold-soft)]/30 px-3 py-1.5 hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] hover:border-[var(--ld-ink)] transition-colors disabled:opacity-60 disabled:cursor-wait"
                      >
                        <FileSignature size={13} strokeWidth={1.6} />
                        {packSendingId === v.id ? "Sending…" : "Send pack"}
                        <StatusDot
                          status={packStatusMap[v.id]?.status}
                          testid={`vendor-list-pack-status-${v.id}`}
                        />
                      </button>
                      <button
                        data-testid={`vendor-list-view-${v.id}`}
                        onClick={() => onView(v)}
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-[var(--ld-ink)]/30 px-3 py-1.5 hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)] transition-colors"
                      >
                        <Eye size={13} strokeWidth={1.6} />
                        Open
                      </button>
                      <RecordActionsMenu
                        kind="vendor"
                        targetId={v.id}
                        email={v.email}
                        title={v.company_name || v.email || "Vendor"}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pagination && (
        <Pagination
          page={pagination.page}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onPageChange={pagination.onPageChange}
          loading={pagination.loading}
        />
      )}
      <WelcomeEmailModal
        target={welcomeTarget}
        onClose={() => setWelcomeTarget(null)}
        onSent={() => {
          refreshWelcomeStatuses();
          refreshNotesSummary();
        }}
      />
    </div>
  );
};

export default VendorListPanel;
